import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { supabase } from '../config/supabase';

export const FeedContext = createContext({});

export const FeedProvider = ({ children }) => {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [ref] = useInView();
    
    // Reset feed when user logs out
    useEffect(() => {
        if (!user) {
            setPosts([]);
            setLoading(false);
            setHasMore(true);
        }
    }, [user]);

    const fetchOrganizationFeed = useCallback(async (orgId, limit = 50, offset = 0) => {
        if (!user || !orgId) return [];
        
        try {
            setLoading(true);
            setError(null);
            const response = await api.get(`/organizations/${orgId}/feed`, {
                params: { limit, offset }
            });
            const newPosts = response.data || [];
            setPosts(newPosts);
            setHasMore(newPosts.length === limit);
            return newPosts;
        } catch (err) {
            console.error('Error fetching organization feed:', err);
            setError(err.message || 'Failed to load feed');
            setPosts([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [user]);

    const fetchProjectFeed = useCallback(async (projectId, limit = 50, offset = 0) => {
        if (!user || !projectId) return [];
        
        try {
            setLoading(true);
            setError(null);
            const response = await api.get(`/projects/${projectId}/feed`, {
                params: { limit, offset }
            });
            const newPosts = response.data || [];
            setPosts(newPosts);
            setHasMore(newPosts.length === limit);
            return newPosts;
        } catch (err) {
            console.error('Error fetching project feed:', err);
            setError(err.message || 'Failed to load feed');
            setPosts([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Real-time updates commented out for now - can be re-enabled with proper feed_posts table subscription
    // useEffect(() => {
    //     if (!user) return;
    //     
    //     const subscription = supabase
    //         .channel('feed_posts_changes')
    //         .on('postgres_changes',
    //             {
    //                 event: '*',
    //                 schema: 'public',
    //                 table: 'feed_posts'
    //             },
    //             (payload) => {
    //                 if (payload.eventType === 'INSERT') {
    //                     setPosts(prev => [payload.new, ...prev]);
    //                 } else if (payload.eventType === 'DELETE') {
    //                     setPosts(prev => prev.filter(post => post.id !== payload.old.id));
    //                 } else if (payload.eventType === 'UPDATE') {
    //                     setPosts(prev =>
    //                         prev.map(post =>
    //                             post.id === payload.new.id ? { ...post, ...payload.new } : post
    //                         )
    //                     );
    //                 }
    //             }
    //         )
    //         .subscribe();
    //
    //     return () => {
    //         supabase.removeChannel(subscription);
    //     };
    // }, [user]);

    // Add a comment to a post
    const addComment = async (postId, content) => {
        if (!user) throw new Error('User not authenticated');
        
        try {

            const { data, error } = await supabase
                .from('comments')
                .insert([
                    {
                        content: content.trim(),
                        post_id: postId,
                        user_id: user.id
                    }
                ])
                .select('*, profiles!inner(*)')
                .single();

            if (error) throw error;

            // Update the post's comments in the local state
            setPosts(prevPosts =>
                prevPosts.map(post =>
                    post.id === postId
                        ? {
                            ...post,
                            comments: [...(post.comments || []), data],
                            comments_count: (post.comments_count || 0) + 1
                        }
                        : post
                )
            );

            return data;
        } catch (error) {
            console.error('Error adding comment:', error);
            throw error;
        }
    };

    // Delete a comment
    const deleteComment = async (commentId, postId) => {
        try {
            const { error } = await supabase
                .from('comments')
                .delete()
                .eq('id', commentId);

            if (error) throw error;

            // Update the post's comments in the local state
            setPosts(prevPosts =>
                prevPosts.map(post =>
                    post.id === postId
                        ? {
                            ...post,
                            comments: post.comments.filter(c => c.id !== commentId),
                            comments_count: Math.max(0, (post.comments_count || 1) - 1)
                        }
                        : post
                )
            );
        } catch (error) {
            console.error('Error deleting comment:', error);
            throw error;
        }
    };

    const value = {
        posts,
        loading,
        error,
        hasMore,
        ref,
        fetchOrganizationFeed,
        fetchProjectFeed,
        addComment,
        deleteComment,
        refresh: (orgId) => {
            setPosts([]);
            setError(null);
            return fetchOrganizationFeed(orgId);
        }
    };

    return (
        <FeedContext.Provider value={value}>
            {children}
        </FeedContext.Provider>
    );
};

export const useFeed = () => {
    const context = useContext(FeedContext);
    if (context === undefined) {
        throw new Error('useFeed must be used within a FeedProvider');
    }
    return context;
};
